/* eslint-disable eqeqeq */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Box,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ModalComponent from '../../../../components/modal-layout/ModalComponent';
import Iconify from '../../../../components/iconify';
import ProjectMemberSchema from '../../schema/projectMemberSchema';

import { addProjectMember } from '../../../../services/projectService';
import { getAllUsers } from '../../../../services/teamService';
import FilterProjectRoles from '../../../../sections/@dashboard/projectDetails/FilterProjectRoles';
import { useStyles } from '../styles/addMemberModalStyles';
import { INITIAL_PROJECT_MEMBER_FORM_STATE } from '../../constants';
import { TEAMMEMBER } from '../../../../Constants/ProjectRoleConstants';

const AddMemberModal = ({ onClose, action, consumedHrs, id, projectDetails, onSubmit, selected, ...props }) => {
  const classes = useStyles();
  const [filteredEmployeeList, setFilteredEmployeeList] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [isActive, setIsActive] = useState(true);

  const handleClose = () => {
    formik.resetForm();
    setSelectedRole('');
    onClose();
  };

  const handleFormSubmit = async (values) => {
    try {
      let response;
      if (action === 'create') {
        response = await addProjectMember(values, id);
      }
      onSubmit(response?.data?.data);
      toast.success(response?.data?.message);
      handleClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: INITIAL_PROJECT_MEMBER_FORM_STATE,
    validationSchema: ProjectMemberSchema(projectDetails, consumedHrs, selected),
    onSubmit: handleFormSubmit,
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userList = await getAllUsers(selectedRole, id);
        setFilteredEmployeeList(userList?.data?.data);
      } catch (e) {
        toast.error(e.message);
      }
    };

    fetchUsers();
  }, [selectedRole, id]);

  useEffect(() => {
    const fetchProjectRoles = async () => {
      try {
        const roles = await FilterProjectRoles(id);
        setProjectRoles(roles);
      } catch (e) {
        toast.error(e.message);
      }
    };
    if (id) fetchProjectRoles();
  }, [id]);

  const { touched, handleSubmit, errors, isSubmitting, setSubmitting, getFieldProps, setFieldValue } = formik;

  const handleIsActive = (event) => {
    setIsActive(event.target.checked);
    setFieldValue('isActive', event.target.checked);
  };

  return (
    <ModalComponent {...props}>
      <Box className={classes.modalHeader}>
        <Typography variant="h5" align="center" className={classes.headerText}>
          Assign Project Role
        </Typography>
        <IconButton color="primary" onClick={handleClose}>
          <Iconify icon="ic:round-clear" />
        </IconButton>
      </Box>

      <Divider className={classes?.divider} />

      <Container maxWidth="sm" style={{ height: '90vh', overflow: 'auto' }}>
        <FormikProvider value={formik}>
          <Form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl fullWidth error={touched.projectRoleId && Boolean(errors.projectRoleId)}>
                <InputLabel>Role</InputLabel>
                <Select
                  {...getFieldProps('projectRoleId')}
                  label="Role"
                  onChange={(event, value) => {
                    setFieldValue('projectRoleId', value?.props?.value);
                    setSelectedRole(value?.props?.children);
                  }}
                >
                  {projectRoles.map((role, i) => (
                    <MenuItem value={role.id} key={i}>
                      {role?.roleName.toLowerCase() === TEAMMEMBER ? 'Team Member' : role?.roleName}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{touched.projectRoleId && errors.projectRoleId}</FormHelperText>
              </FormControl>
              <FormControl fullWidth error={touched.userId && Boolean(errors.userId)}>
                <InputLabel>Name</InputLabel>
                <Select
                  {...getFieldProps('userId')}
                  error={touched.userId && Boolean(errors.userId)}
                  helperText={touched.userId && errors.userId}
                  disabled={action === 'update' || !selectedRole}
                  label="Name"
                  onChange={(event, value) => {
                    setFieldValue('userId', value?.props?.value);
                  }}
                  title={'Please select role first'}
                >
                  {filteredEmployeeList.length ? (
                    filteredEmployeeList.map((employee, i) => (
                      <MenuItem value={employee.id} key={i}>
                        {employee.userName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No user found</MenuItem>
                  )}
                </Select>
                <FormHelperText>{touched.userId && errors.userId}</FormHelperText>
              </FormControl>
              <Grid container justifyContent={'space-between'}>
                <Grid item>
                  <TextField
                    label="Project Available Hours"
                    value={(projectDetails?.totalHrs - consumedHrs).toFixed(2)}
                    disabled
                  />
                </Grid>
                <Grid item>
                  <TextField label="Project Consumed Hours" value={(consumedHrs ?? 0).toFixed(2)} disabled />
                </Grid>
              </Grid>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container direction={'row'} justifyContent={'space-between'}>
                  <Grid item>
                    <Box mb={2}>
                      <DatePicker
                        label={`${isActive ? 'Active From' : 'Inactive From'}`}
                        value={formik.values.startDate}
                        onChange={(newValue) => setFieldValue('startDate', newValue)}
                        inputFormat="DD-MMM-YYYY"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            {...getFieldProps('startDate')}
                            error={touched.startDate && Boolean(errors.startDate)}
                            helperText={touched.startDate && errors.startDate}
                            fullWidth
                            style={{ maxWidth: '225px' }}
                          />
                        )}
                      />
                    </Box>
                  </Grid>
                  {/* <Grid item>
                    <Box mb={2}>
                      <DatePicker
                        label={`${isActive ? 'Active Until' : 'Inactive Until'}`}
                        value={formik.values.endDate}
                        onChange={(newValue) => setFieldValue('endDate', newValue)}
                        inputFormat="DD-MMM-YYYY"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            {...getFieldProps('endDate')}
                            error={touched.endDate && Boolean(errors.endDate)}
                            helperText={touched.endDate && errors.endDate}
                            fullWidth
                            style={{ maxWidth: '225px' }}
                          />
                        )}
                      />
                    </Box>
                  </Grid> */}
                  <Grid item>
                    <Box mb={2}>
                      <TextField
                        label="Add Hours"
                        type="number"
                        {...getFieldProps('userHours')}
                        error={touched.userHours && Boolean(errors.userHours)}
                        helperText={touched.userHours && errors.userHours}
                        fullWidth
                        style={{ maxWidth: '225px' }}
                      />
                    </Box>
                  </Grid>
                  <Grid item sx={{ width: '100%', display: "flex", justifyContent: "flex-end" }}>
                    <Box mb={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            {...getFieldProps('isActive')}
                            name="isActive"
                            color="primary"
                            checked={isActive}
                            onChange={(e) => {
                              handleIsActive(e);
                            }}
                          />
                        }
                        label="Is active"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </LocalizationProvider>
              <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
                Save
              </LoadingButton>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </ModalComponent>
  );
};

AddMemberModal.propTypes = {
  onClose: PropTypes.func,
  action: PropTypes.string,
  consumedHrs: PropTypes.number,
  id: PropTypes.number,
  projectDetails: PropTypes.object,
  onSubmit: PropTypes.func,
  selected: PropTypes.object,
};

export default AddMemberModal;
